import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { useBrand } from "../lib/brand";
import { useAuth } from "../lib/auth";
import { resetTheme } from "../lib/brand";
import { Scissors, ArrowRight, LogOut, Plus, Shield, X } from "lucide-react";

export default function SelectBrandPage() {
  const { brands, ready, select, refresh } = useBrand();
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [showCreate, setShowCreate] = useState(false);

  // The Brand Accounts page is a workspace-neutral surface — always show the
  // default editorial grayscale, never a specific brand's theme.
  useEffect(() => { resetTheme(); }, []);

  useEffect(() => {
    if (!ready) return;
    if (brands.length === 0 && !user?.organization_id) {
      // completely new user → onboarding wizard
      nav("/onboarding", { replace: true });
    }
    // always land on brand selector — never auto-enter
  }, [ready, brands, user, nav]);

  const enter = async (b) => {
    await select(b.id);
    nav("/dashboard");
  };

  const dateLabel = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" }); }
    catch { return "—"; }
  };

  return (
    <div className="min-h-screen bg-[var(--bg,#FAFAFA)] text-[#0A0A0A] p-8" data-testid="select-brand-page">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-10">
          <div className="flex items-center gap-3">
            <Scissors size={22} className="text-[#0A0A0A]" strokeWidth={1.5}/>
            <div>
              <div className="font-serif text-2xl tracking-widest">MMS HUB CENTER</div>
              <div className="label-caps mt-1">Brand Accounts</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {user?.is_super_admin && (
              <button data-testid="super-admin-link" onClick={()=>nav("/admin")} className="btn-ghost inline-flex items-center gap-2"><Shield size={14}/> Super Admin</button>
            )}
            <button data-testid="logout-btn" onClick={async()=>{await logout(); nav("/login");}} className="btn-ghost inline-flex items-center gap-2"><LogOut size={14}/> Logout</button>
          </div>
        </div>

        <div className="mb-10 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <div className="label-caps text-[#0A0A0A] mb-2">Welcome back, {user?.name}</div>
            <h1 className="font-serif text-4xl md:text-5xl font-light">Choose a brand to enter</h1>
            <p className="text-[#525252] text-sm mt-3 max-w-xl">Each brand is a fully isolated workspace — its own collections, garments, files, orders and settings. You can exit anytime to switch brands.</p>
          </div>
          <button data-testid="add-brand-btn" onClick={()=>setShowCreate(true)} className="btn-gold inline-flex items-center gap-2 self-start md:self-end">
            <Plus size={14}/> Add Brand
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {brands.map((b) => {
            const accent = b.theme?.accent_color || "#0A0A0A";
            return (
              <button
                key={b.id}
                data-testid={`brand-card-${b.id}`}
                onClick={() => enter(b)}
                className="card-dark p-6 text-left hover-lift group flex flex-col"
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="w-14 h-14 flex items-center justify-center border" style={{ borderColor: accent + "80", background: accent + "1A" }}>
                    <span className="font-serif text-2xl" style={{ color: accent }}>{b.name?.[0]?.toUpperCase() || "?"}</span>
                  </div>
                  <ArrowRight size={16} className="text-[#525252] group-hover:text-[#0A0A0A] transition-colors"/>
                </div>
                <div className="font-serif text-2xl mb-1">{b.name}</div>
                <div className="text-[10px] uppercase tracking-widest mb-3" style={{ color: accent }}>
                  {b.my_role ? b.my_role.replaceAll("_", " ") : "—"}
                </div>
                <div className="text-[10px] uppercase tracking-widest text-[#737373] mb-4">
                  Last updated {dateLabel(b.updated_at)}
                </div>
                <div className="grid grid-cols-3 gap-2 pt-4 border-t border-[#E5E5E5] mt-auto">
                  <Stat label="Collections" value={b.stats?.collections ?? 0}/>
                  <Stat label="Garments" value={b.stats?.garments ?? 0}/>
                  <Stat label="Open tasks" value={b.stats?.tasks_open ?? 0}/>
                </div>
                <div className="mt-5 pt-4 border-t border-[#E5E5E5] text-[10px] uppercase tracking-widest" style={{ color: accent }}>
                  Enter Workspace →
                </div>
              </button>
            );
          })}

          <button
            data-testid="add-brand-card"
            onClick={()=>setShowCreate(true)}
            className="dropzone p-6 flex flex-col items-center justify-center text-[#525252] hover:text-[#0A0A0A] transition-colors min-h-[260px]"
          >
            <Plus size={32} className="mb-3"/>
            <div className="label-caps">Add Brand</div>
            <div className="text-[10px] text-[#737373] uppercase tracking-widest mt-2">Create a new workspace</div>
          </button>
        </div>
      </div>

      {showCreate && <AddBrandModal onClose={()=>setShowCreate(false)} onCreated={async(b)=>{
        setShowCreate(false);
        await refresh();
        await select(b.id);
        nav("/dashboard");
      }}/>}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="text-[9px] uppercase tracking-widest text-[#737373]">{label}</div>
      <div className="font-serif text-xl">{value}</div>
    </div>
  );
}

function AddBrandModal({ onClose, onCreated }) {
  const { user, refresh } = useAuth();
  const [form, setForm] = useState({ name: "", description: "", accent_color: "#0A0A0A" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true); setError("");
    try {
      // ensure user has an organization first
      if (!user?.organization_id) {
        await api.post("/organizations", {
          name: `${user.name}'s Studio`,
          billing_email: user.email,
        });
        await refresh();
      }
      const { data } = await api.post("/brands", form);
      onCreated(data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={onClose}>
      <div className="card-dark w-full max-w-md p-8" onClick={(e)=>e.stopPropagation()} data-testid="add-brand-modal">
        <div className="flex items-start justify-between mb-6">
          <div>
            <div className="label-caps text-[#0A0A0A] mb-2">New workspace</div>
            <h2 className="font-serif text-2xl">Add a brand</h2>
          </div>
          <button onClick={onClose} className="text-[#525252] hover:text-[#0A0A0A]" data-testid="add-brand-close"><X size={16}/></button>
        </div>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="label-caps block mb-2">Brand name *</label>
            <input required autoFocus data-testid="add-brand-name" className="input-dark" value={form.name} onChange={(e)=>setForm({...form, name: e.target.value})} placeholder="e.g., Kronosports"/>
          </div>
          <div>
            <label className="label-caps block mb-2">Short description</label>
            <input className="input-dark" value={form.description} onChange={(e)=>setForm({...form, description: e.target.value})} placeholder="Optional"/>
          </div>
          <div>
            <label className="label-caps block mb-2">Accent color</label>
            <div className="flex items-center gap-3">
              <input type="color" className="w-12 h-10 border border-[#E5E5E5] bg-transparent cursor-pointer" value={form.accent_color} onChange={(e)=>setForm({...form, accent_color: e.target.value})} data-testid="add-brand-color"/>
              <input className="input-dark flex-1" value={form.accent_color} onChange={(e)=>setForm({...form, accent_color: e.target.value})}/>
            </div>
            <div className="text-[10px] text-[#737373] uppercase tracking-widest mt-2">You can add a logo & background image later in Brand Settings.</div>
          </div>
          {error && <div className="text-red-600 text-sm">{error}</div>}
          <button disabled={saving} className="btn-gold w-full inline-flex items-center justify-center gap-2 disabled:opacity-50" data-testid="add-brand-submit">
            {saving ? "Creating…" : "Create brand & enter"} <ArrowRight size={14}/>
          </button>
        </form>
      </div>
    </div>
  );
}
